import { useEffect, useRef } from 'react';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import useGrowthBookFeatureValue from 'components/GrowthBookProvider/useGrowthBookFeatureValue';
import { quickPurchaseStatusTracker } from 'metrics/user-analytics/purchase';
import { isQuickPurchaseBlockedByOverbooking } from 'utils/quickPurchaseAvailability';
import useSearch from 'hooks/store/useSearch';

/**
 * useQuickPurchaseStatusTracker hook.
 * Fires the "Quick Purchase Status" Mixpanel event once per search results view,
 * after the GrowthBook `isEligibleForQuickPurchase` attribute has been applied so
 * the read of QUICK_PURCHASE_ENABLED already reflects the targeted variant.
 * @param {object} params - Pre-computed eligibility plus search context.
 * @param {boolean} params.isReady - Whether the GrowthBook attribute has been set.
 * @param {boolean} params.isEligibleForQuickPurchase - Pure eligibility (no experiment dependency).
 * @param {boolean} params.anyTripAllowsSeatSelection - Whether at least one trip allows seat selection.
 * @param {boolean} params.hasEnoughPassengers - Whether saved passengers cover the search count.
 * @param {boolean} params.isRoundTrip - Whether the search is round trip.
 * @param {Array} params.qpPassengers - Available quick purchase passengers.
 * @param {string} params.passengersSource - Source of qp passengers.
 */
const useQuickPurchaseStatusTracker = ({
  isReady,
  isEligibleForQuickPurchase,
  anyTripAllowsSeatSelection,
  hasEnoughPassengers,
  isRoundTrip,
  qpPassengers,
  passengersSource,
  trips,
}) => {
  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');
  const gbQuickPurchaseValue = useGrowthBookFeatureValue('quick_purchase_enabled');
  const { departure: searchDeparture } = useSearch();
  const isBulkTicket = searchDeparture?.routeType === 'bulk_ticket';
  const eventFired = useRef(false);

  const hasOverbookingBlockedTrips = Array.isArray(trips)
    ? trips.some(
        (trip) =>
          trip.allowsSeatSelection &&
          !trip.hasConnections &&
          isQuickPurchaseBlockedByOverbooking(trip),
      )
    : false;

  useEffect(() => {
    if (!isReady) return;
    if (gbQuickPurchaseValue === undefined || gbQuickPurchaseValue === null) return;
    if (eventFired.current) return;

    eventFired.current = true;

    const eligible = isEligibleForQuickPurchase;
    const shown = Boolean(quickPurchaseEnabled);

    let reason;
    if (!shown) {
      if (eligible) {
        reason = 'experiment_false';
      } else if (isRoundTrip) {
        reason = 'round_trip';
      } else if (isBulkTicket) {
        reason = 'bulk_ticket';
      } else if (!anyTripAllowsSeatSelection) {
        reason = 'connection_trip';
      } else if (hasOverbookingBlockedTrips) {
        reason = 'overbooking';
      } else if (!hasEnoughPassengers) {
        reason = 'no_passengers';
      }
    }

    quickPurchaseStatusTracker({
      eligible,
      shown,
      reason,
      passengersCount: qpPassengers.length,
      passengersSource,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gbQuickPurchaseValue, isReady]);
};

export default useQuickPurchaseStatusTracker;
